import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { format, parseISO } from 'date-fns';
import { Palmtree, ClipboardList, Loader2, CalendarDays, HeartPulse, BadgePercent, Sparkles, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import { BottomSheet } from '../shared/bottom-sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import { LeaveRequest, CreateLeaveRequestDto } from '../../services/leaveRequestService';
import { payslipBalanceService, PayslipBalanceItem } from '../../services/payslipBalanceService';

type LeaveType = 'vacation' | 'permission' | 'sickness' | 'rol';
type DurationType = 'full_day' | 'half_day_morning' | 'half_day_afternoon' | 'hours';

const leaveTypeMeta: Record<LeaveType, {
  icon: typeof Palmtree;
  labelKey: string;
  defaultLabel: string;
  iconClassName: string;
}> = {
  vacation: {
    icon: Palmtree,
    labelKey: 'leave.type.vacation',
    defaultLabel: 'Vacation',
    iconClassName: 'text-orange-500',
  },
  permission: {
    icon: ClipboardList,
    labelKey: 'leave.type.permission',
    defaultLabel: 'Permission',
    iconClassName: 'text-blue-500',
  },
  sickness: {
    icon: HeartPulse,
    labelKey: 'leave.type.sickness',
    defaultLabel: 'Sickness',
    iconClassName: 'text-rose-500',
  },
  rol: {
    icon: BadgePercent,
    labelKey: 'leave.type.rol',
    defaultLabel: 'ROL',
    iconClassName: 'text-violet-500',
  },
};

interface FormValues {
  type: LeaveType;
  startDate: string;
  endDate: string;
  durationType: DurationType;
  hours: number;
  reason: string;
}

interface LeaveRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateLeaveRequestDto, submit: boolean) => Promise<void>;
  request?: LeaveRequest | null;
  loading?: boolean;
}

const durationOptions: { value: DurationType; labelKey: string; defaultLabel: string }[] = [
  { value: 'full_day', labelKey: 'leave.duration.fullDay', defaultLabel: 'Full day' },
  { value: 'half_day_morning', labelKey: 'leave.duration.halfMorning', defaultLabel: 'Half day (Morning)' },
  { value: 'half_day_afternoon', labelKey: 'leave.duration.halfAfternoon', defaultLabel: 'Half day (Afternoon)' },
  { value: 'hours', labelKey: 'leave.duration.hours', defaultLabel: 'Hours' },
];

export const LeaveRequestDialog: React.FC<LeaveRequestDialogProps> = ({
  open,
  onClose,
  onSave,
  request,
  loading = false,
}) => {
  const { t } = useTranslation();
  const isEditing = !!request;
  const [latestBalance, setLatestBalance] = useState<PayslipBalanceItem | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      type: 'vacation',
      startDate: format(new Date(), 'yyyy-MM-dd'),
      endDate: format(new Date(), 'yyyy-MM-dd'),
      durationType: 'full_day',
      hours: 4,
      reason: '',
    },
  });

  const watchDurationType = watch('durationType');
  const watchType = watch('type');
  const watchStartDate = watch('startDate');
  const watchEndDate = watch('endDate');
  const watchHours = watch('hours');

  const toNumberOrNull = (value: number | string | null | undefined): number | null => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const convertValue = (
    value: number | null,
    fromUnit: 'g' | 'h' | null,
    toUnit: 'g' | 'h',
    hoursPerDay: number,
  ): number | null => {
    if (value === null) {
      return null;
    }

    if (fromUnit === toUnit) {
      return value;
    }

    if (fromUnit === 'h' && toUnit === 'g') {
      return value / hoursPerDay;
    }

    if (fromUnit === 'g' && toUnit === 'h') {
      return value * hoursPerDay;
    }

    return null;
  };

  const formatAmount = (value: number | null): string => {
    if (value === null) {
      return '-';
    }
    return new Intl.NumberFormat('it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const getAmountTone = (value: number | null): string => {
    if (value === null) {
      return 'text-muted-foreground';
    }
    if (value < 0) {
      return 'text-red-600 dark:text-red-400';
    }
    return 'text-emerald-700 dark:text-emerald-400';
  };

  // Calculate working days and hours for the selected date range
  const dateRangeSummary = useMemo(() => {
    if (!watchStartDate || !watchEndDate) return null;

    const start = new Date(watchStartDate);
    const end = new Date(watchEndDate);

    if (end < start) return null;

    // Count working days (exclude weekends)
    let workingDays = 0;
    const current = new Date(start);
    while (current <= end) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays++;
      }
      current.setDate(current.getDate() + 1);
    }

    // Calculate hours based on duration type
    let hours: number;
    if (watchDurationType === 'hours') {
      hours = watchHours || 0;
    } else if (watchDurationType === 'half_day_morning' || watchDurationType === 'half_day_afternoon') {
      hours = workingDays * 4;
    } else {
      hours = workingDays * 8;
    }

    return { workingDays, hours };
  }, [watchStartDate, watchEndDate, watchDurationType, watchHours]);

  useEffect(() => {
    if (!open || isEditing) {
      return;
    }

    let isCancelled = false;
    const loadLatestBalance = async () => {
      try {
        setBalanceLoading(true);
        const balances = await payslipBalanceService.getMy();
        if (!isCancelled) {
          setLatestBalance(balances[0] ?? null);
        }
      } catch (error) {
        if (!isCancelled) {
          setLatestBalance(null);
        }
        console.error('Failed to load payslip balances:', error);
      } finally {
        if (!isCancelled) {
          setBalanceLoading(false);
        }
      }
    };

    void loadLatestBalance();

    return () => {
      isCancelled = true;
    };
  }, [open, isEditing]);

  const leavePlanSummary = useMemo(() => {
    if (!latestBalance || !dateRangeSummary || dateRangeSummary.hours <= 0) {
      return null;
    }

    if (watchType === 'sickness') {
      return null;
    }

    const hoursPerDay = toNumberOrNull(latestBalance.hoursPerDay) ?? 8;
    const requestedHours = dateRangeSummary.hours;
    const requestedDays = requestedHours / hoursPerDay;
    const totalDaysResidualBefore = toNumberOrNull(latestBalance.totalDaysResidual);
    const totalDaysProjectionBefore = toNumberOrNull(latestBalance.totalDaysProjection);
    const totalDaysResidualAfter = totalDaysResidualBefore === null ? null : totalDaysResidualBefore - requestedDays;
    const totalDaysProjectionAfter = totalDaysProjectionBefore === null ? null : totalDaysProjectionBefore - requestedDays;

    const mapByType = watchType === 'vacation'
      ? {
          residuo: toNumberOrNull(latestBalance.ferieResiduo),
          proiezione: toNumberOrNull(latestBalance.ferieProiezione),
          unit: latestBalance.ferieUnit,
          targetUnit: 'g' as const,
          label: t('leave.summary.availableVacation', 'Ferie disponibili'),
        }
      : watchType === 'permission'
        ? {
            residuo: toNumberOrNull(latestBalance.permessiResiduo),
            proiezione: toNumberOrNull(latestBalance.permessiProiezione),
            unit: latestBalance.permessiUnit,
            targetUnit: 'h' as const,
            label: t('leave.summary.availablePermission', 'Permessi disponibili'),
          }
        : {
            residuo: toNumberOrNull(latestBalance.rolResiduo),
            proiezione: toNumberOrNull(latestBalance.rolProiezione),
            unit: latestBalance.rolUnit,
            targetUnit: latestBalance.rolUnit === 'g' ? ('g' as const) : ('h' as const),
            label: t('leave.summary.availableRol', 'ROL disponibili'),
          };

    const requestedValue = mapByType.targetUnit === 'h' ? requestedHours : requestedDays;
    const beforeResidual = convertValue(mapByType.residuo, mapByType.unit, mapByType.targetUnit, hoursPerDay);
    const beforeProjection = convertValue(mapByType.proiezione, mapByType.unit, mapByType.targetUnit, hoursPerDay);
    const afterResidual = beforeResidual === null ? null : beforeResidual - requestedValue;
    const afterProjection = beforeProjection === null ? null : beforeProjection - requestedValue;

    return {
      label: mapByType.label,
      targetUnit: mapByType.targetUnit,
      requestedValue,
      requestedDays,
      beforeResidual,
      afterResidual,
      beforeProjection,
      afterProjection,
      totalDaysResidualBefore,
      totalDaysResidualAfter,
      totalDaysProjectionBefore,
      totalDaysProjectionAfter,
      periodLabel: `${latestBalance.payslipMonth}/${latestBalance.payslipYear}`,
    };
  }, [latestBalance, dateRangeSummary, watchType, t]);

  // Reset form when dialog opens or request changes
  useEffect(() => {
    if (open) {
      if (request) {
        reset({
          type: request.type,
          startDate: format(parseISO(request.startDate), 'yyyy-MM-dd'),
          endDate: format(parseISO(request.endDate), 'yyyy-MM-dd'),
          durationType: request.durationType,
          hours: request.hours || 4,
          reason: request.reason || '',
        });
      } else {
        reset({
          type: 'vacation',
          startDate: format(new Date(), 'yyyy-MM-dd'),
          endDate: format(new Date(), 'yyyy-MM-dd'),
          durationType: 'full_day',
          hours: 4,
          reason: '',
        });
      }
    }
  }, [open, request, reset]);

  const processFormData = (data: FormValues): CreateLeaveRequestDto => {
    const dto: CreateLeaveRequestDto = {
      type: data.type,
      startDate: data.startDate,
      endDate: data.endDate,
      durationType: data.durationType,
    };

    if (data.durationType === 'hours') {
      dto.hours = data.hours;
    }

    if (data.reason.trim()) {
      dto.reason = data.reason.trim();
    }

    return dto;
  };

  const handleSaveDraft = handleSubmit(async (data) => {
    const dto = processFormData(data);
    await onSave(dto, false);
  });

  const handleSubmitRequest = handleSubmit(async (data) => {
    const dto = processFormData(data);
    await onSave(dto, true);
  });

  const handleClose = () => {
    if (!isSubmitting && !loading) {
      onClose();
    }
  };

  const selectedTypeMeta = leaveTypeMeta[watchType];
  const TypeIcon = selectedTypeMeta.icon;
  const dialogTitle = isEditing
    ? t('leave.dialog.editTitle', 'Edit Leave Request')
    : t('leave.dialog.newTitle', 'New Leave Request');
  const dialogDescription = isEditing
    ? t('leave.dialog.editDescription', 'Update your leave request details')
    : t('leave.dialog.newDescription', 'Create a new leave request');
  const canSaveDraft = !isEditing || request?.state !== 'approved';

  return (
    <BottomSheet
      open={open}
      onClose={handleClose}
      title={dialogTitle}
      description={dialogDescription}
      contentClassName="pb-0"
    >
      <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
        <TypeIcon
          className={cn(
            'h-4 w-4',
            selectedTypeMeta.iconClassName
          )}
        />
        <span className="text-muted-foreground">
          {t(selectedTypeMeta.labelKey, selectedTypeMeta.defaultLabel)}
        </span>
      </div>

      <form className="space-y-4 py-4 pb-24">
          {/* Type Selection */}
          <div className="space-y-2">
            <Label htmlFor="type">{t('leave.form.type', 'Type')}</Label>
            <Controller
              name="type"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="type">
                    <SelectValue placeholder={t('leave.form.selectType', 'Select type')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vacation">
                      <span className="flex items-center gap-2">
                        <Palmtree className="h-4 w-4 text-orange-500" />
                        {t('leave.type.vacation', 'Vacation')}
                      </span>
                    </SelectItem>
                    <SelectItem value="permission">
                      <span className="flex items-center gap-2">
                        <ClipboardList className="h-4 w-4 text-blue-500" />
                        {t('leave.type.permission', 'Permission')}
                      </span>
                    </SelectItem>
                    <SelectItem value="sickness">
                      <span className="flex items-center gap-2">
                        <HeartPulse className="h-4 w-4 text-rose-500" />
                        {t('leave.type.sickness', 'Sickness')}
                      </span>
                    </SelectItem>
                    <SelectItem value="rol">
                      <span className="flex items-center gap-2">
                        <BadgePercent className="h-4 w-4 text-violet-500" />
                        {t('leave.type.rol', 'ROL')}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {/* Date Selection */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="startDate">{t('leave.form.startDate', 'Start Date')}</Label>
              <Controller
                name="startDate"
                control={control}
                rules={{ required: t('leave.validation.startDateRequired', 'Start date is required') }}
                render={({ field }) => (
                  <Input
                    id="startDate"
                    type="date"
                    {...field}
                    className={errors.startDate ? 'border-red-500' : ''}
                  />
                )}
              />
              {errors.startDate && (
                <p className="text-xs text-red-500">{errors.startDate.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">{t('leave.form.endDate', 'End Date')}</Label>
              <Controller
                name="endDate"
                control={control}
                rules={{
                  required: t('leave.validation.endDateRequired', 'End date is required'),
                  validate: (value, formValues) => {
                    if (value < formValues.startDate) {
                      return t('leave.validation.endDateAfterStart', 'End date must be on or after start date');
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <Input
                    id="endDate"
                    type="date"
                    {...field}
                    className={errors.endDate ? 'border-red-500' : ''}
                  />
                )}
              />
              {errors.endDate && (
                <p className="text-xs text-red-500">{errors.endDate.message}</p>
              )}
            </div>
          </div>

          {/* Date Range Summary */}
          {dateRangeSummary && dateRangeSummary.workingDays > 0 && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-muted/50 text-sm">
              <CalendarDays className="h-4 w-4 text-orange-500" />
              <span>
                {dateRangeSummary.workingDays === 1
                  ? t('leave.summary.oneDay', '1 working day')
                  : t('leave.summary.days', '{{count}} working days', { count: dateRangeSummary.workingDays })}
                {' '}
                <span className="text-muted-foreground">
                  ({dateRangeSummary.hours}h)
                </span>
              </span>
            </div>
          )}

          {/* Duration Type */}
          <div className="space-y-3">
            <Label>{t('leave.form.duration', 'Duration Type')}</Label>
            <Controller
              name="durationType"
              control={control}
              render={({ field }) => (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {durationOptions.map((option) => (
                    <label
                      key={option.value}
                      className={cn(
                        'flex items-center gap-2 rounded-md border p-3 cursor-pointer transition-colors',
                        'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-orange-500 has-[:focus-visible]:ring-offset-2',
                        field.value === option.value
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-950'
                          : 'border-input hover:bg-accent'
                      )}
                    >
                      <input
                        type="radio"
                        name="durationType"
                        value={option.value}
                        checked={field.value === option.value}
                        onChange={() => field.onChange(option.value)}
                        className="sr-only"
                      />
                      <div
                        className={cn(
                          'h-4 w-4 rounded-full border-2 flex items-center justify-center',
                          field.value === option.value
                            ? 'border-orange-500'
                            : 'border-muted-foreground'
                        )}
                      >
                        {field.value === option.value && (
                          <div className="h-2 w-2 rounded-full bg-orange-500" />
                        )}
                      </div>
                      <span className="text-sm">{t(option.labelKey, option.defaultLabel)}</span>
                    </label>
                  ))}
                </div>
              )}
            />
          </div>

          {/* Hours Input (conditional) */}
          {watchDurationType === 'hours' && (
            <div className="space-y-2">
              <Label htmlFor="hours">{t('leave.form.hours', 'Hours')}</Label>
              <Controller
                name="hours"
                control={control}
                rules={{
                  required: watchDurationType === 'hours' ? t('leave.validation.hoursRequired', 'Hours is required') : false,
                  min: { value: 0.5, message: t('leave.validation.hoursMin', 'Minimum 0.5 hours') },
                  max: { value: 8, message: t('leave.validation.hoursMax', 'Maximum 8 hours') },
                }}
                render={({ field }) => (
                  <Input
                    id="hours"
                    type="number"
                    min={0.5}
                    max={8}
                    step={0.5}
                    {...field}
                    onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                    className={cn('w-32', errors.hours ? 'border-red-500' : '')}
                  />
                )}
              />
              {errors.hours && (
                <p className="text-xs text-red-500">{errors.hours.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {t('leave.form.hoursRequired', 'Enter hours between 0.5 and 8')}
              </p>
            </div>
          )}

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">
              {t('leave.form.reason', 'Reason')}
              <span className="text-muted-foreground ml-1">
                ({t('common.optional', 'optional')})
              </span>
            </Label>
            <Controller
              name="reason"
              control={control}
              render={({ field }) => (
                <Textarea
                  id="reason"
                  placeholder={t('leave.form.reasonPlaceholder', 'Enter reason for leave request...')}
                  rows={3}
                  {...field}
                />
              )}
            />
          </div>

          {!isEditing && watchType !== 'sickness' && (
            <div className="rounded-xl border bg-muted/30 p-4 text-sm shadow-sm space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-orange-700">
                  <Sparkles className="h-4 w-4" />
                  {t('leave.summary.planTitle', 'Piano ferie/permessi')}
                </p>
              </div>

              {balanceLoading && (
                <p className="text-muted-foreground">{t('common.loading', 'Loading...')}</p>
              )}

              {!balanceLoading && !leavePlanSummary && (
                <p className="text-muted-foreground">
                  {t('leave.summary.noBalance', 'Nessun saldo busta paga disponibile per il calcolo pre/post.')}
                </p>
              )}

              {!balanceLoading && leavePlanSummary && (
                <>
                  <div className="rounded-lg border bg-card p-3">
                    <p className="font-medium text-foreground">{leavePlanSummary.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('leave.summary.periodRef', 'riferimento')}: {leavePlanSummary.periodLabel}
                    </p>
                  </div>

                  <Card className="border-orange-200 bg-orange-50/60 shadow-none dark:border-orange-900/60 dark:bg-orange-950/35">
                    <CardContent className="p-3 bg-transparent">
                      <p className="text-xs font-semibold uppercase tracking-wide text-orange-700 dark:text-orange-300">
                        {t('leave.summary.requestImpact', 'Impatto richiesta')}
                      </p>
                      <p className="mt-1 text-lg font-bold text-orange-800 dark:text-orange-200">
                        - {formatAmount(leavePlanSummary.requestedValue)} {leavePlanSummary.targetUnit}
                      </p>
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Card>
                      <CardContent className="p-3 space-y-1">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {t('leave.summary.beforeResidual', 'Residuo prima')} / {t('leave.summary.afterResidual', 'dopo')}
                        </p>
                        <p className="text-sm">
                          Prima: <strong>{formatAmount(leavePlanSummary.beforeResidual)} {leavePlanSummary.targetUnit}</strong>
                        </p>
                        <p className="text-sm">
                          Dopo:{' '}
                          <strong className={getAmountTone(leavePlanSummary.afterResidual)}>
                            {formatAmount(leavePlanSummary.afterResidual)} {leavePlanSummary.targetUnit}
                          </strong>
                        </p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="p-3 space-y-1">
                        <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          <TrendingUp className="h-3.5 w-3.5" />
                          {t('leave.summary.beforeProjection', 'Proiezione prima')} / {t('leave.summary.afterProjection', 'dopo')}
                        </p>
                        <p className="text-sm">
                          Prima: <strong>{formatAmount(leavePlanSummary.beforeProjection)} {leavePlanSummary.targetUnit}</strong>
                        </p>
                        <p className="text-sm">
                          Dopo:{' '}
                          <strong className={getAmountTone(leavePlanSummary.afterProjection)}>
                            {formatAmount(leavePlanSummary.afterProjection)} {leavePlanSummary.targetUnit}
                          </strong>
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  <Card className="border-emerald-200 bg-emerald-50/70 shadow-none dark:border-emerald-900/60 dark:bg-emerald-950/30">
                    <CardContent className="p-3 bg-transparent space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-300">
                        {t('leave.summary.totalOverall', 'Totale complessivo giorni da consumare')}
                      </p>
                      <p className="text-sm text-foreground">
                        {t('leave.summary.beforeProjection', 'Proiezione prima')}: <strong>{formatAmount(leavePlanSummary.totalDaysProjectionBefore)} g</strong>
                        {' '}
                        • {t('leave.summary.afterProjection', 'dopo')}: <strong className={getAmountTone(leavePlanSummary.totalDaysProjectionAfter)}>{formatAmount(leavePlanSummary.totalDaysProjectionAfter)} g</strong>
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t('leave.summary.beforeResidual', 'Residuo prima')}: <strong>{formatAmount(leavePlanSummary.totalDaysResidualBefore)} g</strong>
                        {' '}
                        • {t('leave.summary.afterResidual', 'dopo')}: <strong className={getAmountTone(leavePlanSummary.totalDaysResidualAfter)}>{formatAmount(leavePlanSummary.totalDaysResidualAfter)} g</strong>
                      </p>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          )}
      </form>

      <div className="sticky bottom-0 z-30 -mx-4 mt-4 border-t bg-background px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3 shadow-[0_-10px_20px_-14px_rgba(0,0,0,0.45)]">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting || loading}
            className="whitespace-nowrap"
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          {canSaveDraft && (
            <Button
              variant="outline"
              onClick={handleSaveDraft}
              disabled={isSubmitting || loading}
              className="whitespace-nowrap"
            >
              {(isSubmitting || loading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('leave.form.saveDraft', 'Save Draft')}
            </Button>
          )}
          <Button
            onClick={handleSubmitRequest}
            disabled={isSubmitting || loading}
            className="whitespace-nowrap bg-orange-500 hover:bg-orange-600"
          >
            {(isSubmitting || loading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('leave.form.submitRequest', 'Submit Request')}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
};

export default LeaveRequestDialog;
